import { doc, runTransaction, serverTimestamp, deleteDoc, getDoc, collection, getDocs, query, where, onSnapshot, writeBatch, setDoc } from 'firebase/firestore';
import { newReservationLogRef, writeReservationLogTx } from './reservationLogsService';
import { db } from '../../sdk_initialization';
import { getLayoutPublic } from './layoutsService';
import { getFunctions, httpsCallable } from 'firebase/functions';
import app from '../../sdk_initialization';

// Simple in-memory cache to avoid refetching layouts repeatedly within a session.
const layoutCache = new Map(); // layoutId -> layout object
const getLayoutCached = async (layoutId) => {
  if (!layoutId) return null;
  if (layoutCache.has(layoutId)) return layoutCache.get(layoutId);
  try {
    const ly = await getLayoutPublic(layoutId);
    layoutCache.set(layoutId, ly || null);
    return ly || null;
  } catch {
    return null;
  }
};
const resolveBoothNumber = (layout, boothId) => {
  if (!boothId) return null;
  try {
    const cells = Array.isArray(layout?.cellData) ? layout.cellData : [];
    for (const c of cells) {
      if (!c || !c.id) continue;
      if (c.id === boothId) {
        if (c.number != null) return c.number;
        if (c.boothNumber != null) return c.boothNumber;
        break;
      }
    }
    // Fallback heuristics
    if (typeof boothId === 'string' && /^\d+$/.test(boothId)) return parseInt(boothId, 10);
    return boothId;
  } catch { return null; }
};

const COLLECTION = 'boothReservations';

const reservationDocId = ({ eventId, layoutId, boothId }) => `${eventId}_${layoutId}_${boothId}`;

const logManageReservationFailure = async ({ action, payload, data, message, code }) => {
  // Client-side reservation logging is intentionally disabled.
  // Logging for userManageReservation actions is now handled in the backend callable.
  return;
  /*
  try {
    const eventId = payload?.eventId || null;
    const layoutId = payload?.layoutId || null;
    const boothId = payload?.boothId || null;
    const reservationId = (eventId && layoutId && boothId)
      ? reservationDocId({ eventId, layoutId, boothId })
      : null;
    await writeReservationLog({
      type: 'user-manage-reservation-failed',
      reservationId,
      eventId,
      layoutId,
      boothId,
      triggeredBy: `userManageReservation:${action || 'unknown'}`,
      triggeredByProfileId: payload?.createdByProfileId || null,
      metadata: {
        message: message || null,
        code: code || null,
        action: action || null,
        response: data || null,
      },
    });
  } catch (e) {
    console.error('[logManageReservationFailure] failed to write reservation log', {
      action,
      eventId: payload?.eventId || null,
      layoutId: payload?.layoutId || null,
      boothId: payload?.boothId || null,
      errorCode: e?.code || null,
      errorMessage: e?.message || String(e),
    });
  }
  */
};

const logManageReservationException = async ({ action, payload, err }) => {
  // Client-side reservation logging is intentionally disabled.
  // Logging for userManageReservation actions is now handled in the backend callable.
  return;
  /*
  try {
    const eventId = payload?.eventId || null;
    const layoutId = payload?.layoutId || null;
    const boothId = payload?.boothId || null;
    const reservationId = (eventId && layoutId && boothId)
      ? reservationDocId({ eventId, layoutId, boothId })
      : null;
    await writeReservationLog({
      type: 'user-manage-reservation-exception',
      reservationId,
      eventId,
      layoutId,
      boothId,
      triggeredBy: `userManageReservation:${action || 'unknown'}`,
      triggeredByProfileId: payload?.createdByProfileId || null,
      metadata: {
        message: err?.message || null,
        code: err?.code || null,
        name: err?.name || null,
        action: action || null,
        details: err?.details || null,
      },
    });
  } catch (e) {
    console.error('[logManageReservationException] failed to write reservation log', {
      action,
      eventId: payload?.eventId || null,
      layoutId: payload?.layoutId || null,
      boothId: payload?.boothId || null,
      errorCode: e?.code || null,
      errorMessage: e?.message || String(e),
    });
  }
  */
};

const ensureCallableSuccess = async (data, defaultCode, defaultMessage, context = {}) => {
  const success = (data && typeof data === 'object') ? data.success : undefined;
  if (success !== true) {
    const message = (data && typeof data === 'object' && data.message)
      ? data.message
      : defaultMessage;
    const code = (data && typeof data === 'object' && data.code)
      ? data.code
      : defaultCode;
    await logManageReservationFailure({
      action: context.action,
      payload: context.payload,
      data,
      message,
      code,
    });
    const err = new Error(message || defaultMessage);
    err.code = code || defaultCode;
    err.details = data;
    err.loggedToReservationLogs = true;
    throw err;
  }
  return data;
};

// Remote callable reservation functions (callable Cloud Function)
export const reserveBoothRemote = async (params) => {

  const functions = getFunctions(app);
  const fn = httpsCallable(functions, 'userManageReservation');
  const payload = { ...(params || {}), action: 'setpendingcart', triggeredBy: 'Cart', type: 'Added' };
  try {
    //console.log('[reserveBoothRemote] Calling userManageReservation with payload:', payload);
    const res = await fn(payload);
    const data = await ensureCallableSuccess(
      res?.data,
      'reserve-failed',
      'Unable to reserve booth',
      { action: 'setpendingcart', payload }
    );
    //console.log('[reserveBoothRemote] userManageReservation response:', res);
    return data && data.reservationId ? data.reservationId : data;
  } catch (err) {
    if (!err?.loggedToReservationLogs) {
      await logManageReservationException({ action: 'setpendingcart', payload, err });
    }
    console.error('[reserveBoothRemote] userManageReservation error:', err);
    throw err;
  }
};

export const releaseBoothRemote = async ({ eventId, layoutId, boothId, vendorId, createdByProfileId, sessionId, triggeredBy, type, refund }) => {
  const functions = getFunctions(app);
  const fn = httpsCallable(functions, 'userManageReservation');
  //console.log('[releaseBoothRemote] Calling userManageReservation with params:', { action: 'release', eventId, layoutId, boothId, vendorId });
  try {
    const payload = { action: 'release', eventId, layoutId, boothId, vendorId, createdByProfileId, sessionId, triggeredBy: triggeredBy || 'release', ...(type ? { type } : {}), ...(refund ? { refund: true } : {}) };
    const res = await fn(payload);
    const data = await ensureCallableSuccess(
      res?.data,
      'release-failed',
      'Unable to release booth',
      { action: 'release', payload }
    );
    //console.log('[releaseBoothRemote] userManageReservation response:', res);
    return data && data.success ? true : !!data;
  } catch (err) {
    if (!err?.loggedToReservationLogs) {
      await logManageReservationException({ action: 'release', payload: { eventId, layoutId, boothId, vendorId, createdByProfileId, sessionId, triggeredBy: triggeredBy || 'release', ...(type ? { type } : {}) }, err });
    }
    console.error('[releaseBoothRemote] userManageReservation error:', err);
    throw err;
  }
};



/**
 * Mark a booth as permanently Reserved (after successful payment)
 * via backend callable to keep transaction logic server-authoritative.
 */
export const markReservedRemote = async ({ eventId, layoutId, boothId, vendorId, createdByProfileId, sessionId, triggeredBy, type }) => {
  if (!eventId || !layoutId || !boothId || !vendorId) {
    throw new Error('Missing required fields for markReservedRemote');
  }
  const functions = getFunctions(app);
  const fn = httpsCallable(functions, 'userManageReservation');
  try {
    const payload = { action: 'reserve', eventId, layoutId, boothId, vendorId, createdByProfileId, sessionId, triggeredBy: triggeredBy || 'Payment', type: type || 'Complete' };
    const res = await fn(payload);
    const data = await ensureCallableSuccess(
      res?.data,
      'reserve-failed',
      'Unable to finalize reservation',
      { action: 'reserve', payload }
    );
    return data?.success ? true : !!data;
  } catch (err) {
    if (!err?.loggedToReservationLogs) {
      await logManageReservationException({ action: 'reserve', payload: { eventId, layoutId, boothId, vendorId, createdByProfileId, sessionId, triggeredBy: triggeredBy || 'Payment', type: type || 'Complete' }, err });
    }
    console.error('[markReservedRemote] userManageReservation error:', err);
    throw err;
  }
};

/**
 * Transition a single cart booth from pendingType="cart" to pendingType="payment".
 * Call once per booth from checkout to keep backend action handling simple.
 */
export const setPendingPaymentRemote = async ({ eventId, layoutId, boothId, boothNumber, vendorId, createdByProfileId, sessionId, paymentIntentId, checkoutLockId, paymentWaitMinutes }) => {
  if (!eventId || !layoutId || !boothId || !vendorId) {
    throw new Error('Missing required fields for setPendingPaymentRemote');
  }
  const functions = getFunctions(app);
  const fn = httpsCallable(functions, 'userManageReservation');
  const payload = {
    action: 'setpendingpayment',
    eventId,
    layoutId,
    vendorId,
    createdByProfileId,
    sessionId,
    paymentIntentId,
    checkoutLockId,
    paymentWaitMinutes,
    boothId,
    ...(boothNumber != null ? { boothNumber } : {}),
    triggeredBy: 'Checkout',
    type: 'Started',
  };
  try {
    const res = await fn(payload);
    const data = await ensureCallableSuccess(
      res?.data,
      'setpendingpayment-failed',
      'Unable to mark booths for payment',
      { action: 'setpendingpayment', payload }
    );
    return data?.success ? true : !!data;
  } catch (err) {
    if (!err?.loggedToReservationLogs) {
      await logManageReservationException({ action: 'setpendingpayment', payload, err });
    }
    console.error('[setPendingPaymentRemote] userManageReservation error:', err);
    throw err;
  }
};

/**
 * Load current reservations for a layout (both Pending and Reserved).
 * Returns a simple map: { [boothId]: status }
 */
export const loadReservationsForLayout = async ({ eventId, layoutId }) => {
  if (!db) throw new Error('Database not initialized');
  if (!eventId || !layoutId) return {};
  const col = collection(db, COLLECTION);
  const q = query(col, where('eventId', '==', eventId), where('layoutId', '==', layoutId));
  const snap = await getDocs(q);
  const map = {};
  snap.forEach(d => {
    const { boothId, status } = d.data() || {};
    if (boothId && status) map[boothId] = status;
  });
  return map;
};

/**
 * Load current reservations for a layout with metadata used by UI availability rules.
 * Returns: { [boothId]: { status, createdAt, expiresAt, expiresAtMs, vendorId, createdByProfileId } }
 */
export const loadReservationsForLayoutDetailed = async ({ eventId, layoutId }) => {
  if (!db) throw new Error('Database not initialized');
  if (!eventId || !layoutId) return {};
  const col = collection(db, COLLECTION);
  const q = query(col, where('eventId', '==', eventId), where('layoutId', '==', layoutId));
  const snap = await getDocs(q);
  const map = {};
  snap.forEach(d => {
    const data = d.data() || {};
    const { boothId, status, createdAt, expiresAt, expiresAtMs, vendorId, createdByProfileId } = data;
    if (boothId && status) {
      map[boothId] = { status, createdAt, expiresAt, expiresAtMs, vendorId, createdByProfileId };
    }
  });
  return map;
};

/**
 * Subscribe to reservations for a layout and receive live updates.
 * onChange receives a map of { [boothId]: status } for all matching docs.
 * Returns the unsubscribe function.
 */
export const subscribeReservationsForLayout = ({ eventId, layoutId, onChange }) => {
  if (!db) throw new Error('Database not initialized');
  if (!eventId || !layoutId || typeof onChange !== 'function') return () => {};
  const col = collection(db, COLLECTION);
  const q = query(col, where('eventId', '==', eventId), where('layoutId', '==', layoutId));
  const unsubscribe = onSnapshot(q, (snap) => {
    const map = {};
    snap.forEach(d => {
      const { boothId, status, createdAt, expiresAt, vendorId, createdByProfileId } = d.data() || {};
      if (boothId && status) map[boothId] = { status, createdAt, expiresAt, vendorId, createdByProfileId };
    });
    onChange(map);
  }, (error) => {
    console.warn('Reservations subscribe error:', error?.message || error);
  });
  return unsubscribe;
};

/**
 * Subscribe to reservations owned by a specific vendor/session for a layout.
 * Emits an array of docs: { boothId, status, createdAt }
 */
export const subscribeMyReservationsForSession = ({ eventId, layoutId, vendorId, sessionId, onChange }) => {
  if (!db) throw new Error('Database not initialized');
  if (!eventId || !layoutId || !vendorId || !sessionId || typeof onChange !== 'function') return () => {};
  const col = collection(db, COLLECTION);
  const q = query(
    col,
    where('eventId', '==', eventId),
    where('layoutId', '==', layoutId),
    where('vendorId', '==', vendorId),
    where('sessionId', '==', sessionId)
  );
  const unsubscribe = onSnapshot(q, (snap) => {
    const rows = [];
    snap.forEach(d => {
      const data = d.data() || {};
      rows.push({ boothId: data.boothId, status: data.status, createdAt: data.createdAt, expiresAt: data.expiresAt, pendingType: data.pendingType || null });
    });
    onChange(rows);
  }, (error) => {
    console.warn('My reservations subscribe error:', error?.message || error);
  });
  return unsubscribe;
};

/**
 * Admin: Put one or more booths On-Hold (unavailable to vendors).
 * Creates/overwrites reservation docs with status 'On-Hold'.
 */
export const setAdminHold = async ({ eventId, layoutId, boothIds = [], createdBy, reason, holdName, expiresAt }) => {
  if (!db) throw new Error('Database not initialized');
  if (!eventId || !layoutId) throw new Error('Missing required fields for setAdminHold');
  const ids = Array.isArray(boothIds) ? boothIds.filter(Boolean) : [];
  if (ids.length === 0) return 0;
  const batch = writeBatch(db);
  let setCount = 0;

  const parseToMs = (ts) => {
    try {
      if (!ts) return null;
      if (typeof ts.toMillis === 'function') return ts.toMillis();
      if (ts && typeof ts.seconds === 'number') return ts.seconds * 1000;
      if (typeof ts === 'number') return ts;
      if (typeof ts === 'string') {
        const p = Date.parse(ts);
        if (!isNaN(p)) return p;
        const m = String(ts).match(/(.+?)\s*UTC([+-]?\d{1,2})\b/i);
        if (m) {
          const datePart = m[1].replace(/\s+at\s+/i, ' ');
          const offsetHours = parseInt(m[2], 10);
          const parsed = Date.parse(datePart + ' UTC');
          if (!isNaN(parsed)) return parsed - (offsetHours * 60 * 60 * 1000);
        }
        const dt = new Date(ts);
        if (!isNaN(dt.getTime())) return dt.getTime();
      }
      const dt = new Date(ts);
      if (!isNaN(dt.getTime())) return dt.getTime();
    } catch (e) {}
    return null;
  };

  // Normalize provided expiresAt for hold (accepts ms, Date, or parseable string)
  const normalizeExpiresAtDate = (v) => {
    if (!v) return null;
    if (v instanceof Date) return v;
    if (typeof v === 'number') return new Date(v);
    if (typeof v === 'string') {
      const p = Date.parse(v);
      if (!isNaN(p)) return new Date(p);
      const m = String(v).match(/(.+?)\s*UTC([+-]?\d{1,2})\b/i);
      if (m) {
        const datePart = m[1].replace(/\s+at\s+/i, ' ');
        const offsetHours = parseInt(m[2], 10);
        const parsed = Date.parse(datePart + ' UTC');
        if (!isNaN(parsed)) return new Date(parsed - (offsetHours * 60 * 60 * 1000));
      }
    }
    const dt = new Date(v);
    if (!isNaN(dt.getTime())) return dt;
    return null;
  };

  for (const boothId of ids) {
    const id = reservationDocId({ eventId, layoutId, boothId });
    const ref = doc(db, COLLECTION, id);

    // Inspect existing reservation to avoid overwriting an active Pending.
    try {
      const snap = await getDoc(ref);
      if (snap.exists()) {
        const data = snap.data() || {};
        // If existing is Pending, only allow overwrite when it's expired
        if (data.status === 'Pending') {
          const now = Date.now();
          const createdMs = parseToMs(data.createdAt);
          const expiresMs = (parseToMs(data.expiresAt) || (createdMs ? (createdMs + (10 * 60 * 1000)) : null));
          const isExpired = typeof expiresMs === 'number' && expiresMs <= now;
          if (!isExpired) {
            // Skip setting the hold for active pending reservation
            console.warn('[setAdminHold] Skipping hold for booth (active Pending):', id);
            continue;
          }
          // expired -> allow overwrite
        }
      }
    } catch (e) {
      // If inspection fails, proceed cautiously and skip this booth
      console.warn('[setAdminHold] Failed to inspect existing reservation for', ref.id, e?.message || e);
      continue;
    }

    const hold = {
      eventId,
      layoutId,
      boothId,
      status: 'On-Hold',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdBy: createdBy || 'admin',
    };
    if (reason) hold.reason = reason;
    if (holdName) hold.holdName = holdName;
    if (expiresAt) {
      const nd = normalizeExpiresAtDate(expiresAt);
      if (nd) hold.expiresAt = nd;
    }
    batch.set(ref, hold, { merge: true });
    setCount++;
  }

  if (setCount > 0) await batch.commit();
  return setCount;
};

/**
 * Admin: Clear On-Hold for one or more booths (make them available again).
 * Deletes the reservation docs for the given booths if they are On-Hold.
 */
export const clearAdminHold = async ({ eventId, layoutId, boothIds = [], actorProfileId = null }) => {
  if (!db) throw new Error('Database not initialized');
  if (!eventId || !layoutId) throw new Error('Missing required fields for clearAdminHold');
  const ids = Array.isArray(boothIds) ? boothIds.filter(Boolean) : [];
  if (ids.length === 0) return 0;
  const batch = writeBatch(db);
  // Load layout once to map boothIds -> boothNumber
  let layout = null;
  try { layout = await getLayoutCached(layoutId); } catch {}
  // Load each doc so we can log details before delete
  for (const boothId of ids) {
    const id = reservationDocId({ eventId, layoutId, boothId });
    const ref = doc(db, COLLECTION, id);
    try {
      const snap = await getDoc(ref);
      if (snap.exists()) {
        const data = snap.data() || {};
        if (data.status === 'On-Hold') {
          try {
            const logRef = newReservationLogRef();
            writeReservationLogTx(batch, logRef, {
              type: 'hold-cleared',
              reservationId: ref.id,
              eventId,
              layoutId,
              boothId,
              ...(resolveBoothNumber(layout, boothId) != null ? { boothNumber: resolveBoothNumber(layout, boothId) } : {}),
              previousStatus: data.status,
              previousVendorId: data.vendorId || null,
              previousCreatedBy: data.createdByProfileId || null,
              previousSessionId: data.sessionId || null,
              previousCreatedAt: data.createdAt || null,
              previousExpiresAt: data.expiresAt || null,
              triggeredBy: 'clearAdminHold',
              triggeredByProfileId: actorProfileId,
              metadata: {},
            });
          } catch {}
        }
      }
    } catch {}
    batch.delete(ref);
  }
  await batch.commit();
  return ids.length;
};

/**
 * List existing hold groups for an event/layout, grouped by holdName.
 * Returns an array of { holdName, count, boothIds } sorted by name.
 */
export const listHoldGroups = async ({ eventId, layoutId }) => {
  if (!db) throw new Error('Database not initialized');
  if (!eventId || !layoutId) return [];
  const col = collection(db, COLLECTION);
  const q = query(col, where('eventId', '==', eventId), where('layoutId', '==', layoutId));
  const snap = await getDocs(q);
  const groups = new Map();
  snap.forEach(d => {
    const data = d.data() || {};
    if (data.status === 'On-Hold' && data.holdName) {
      const key = String(data.holdName);
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push({
        boothId: data.boothId,
        reason: data.reason || '',
        expiresAt: data.expiresAt || null,
      });
    }
  });
  const arr = Array.from(groups.entries()).map(([holdName, items]) => {
    const boothIds = items.map(i => i.boothId);
    const count = boothIds.length;
    // Pick most common non-empty reason
    const reasonCounts = new Map();
    let topReason = '';
    let topCount = 0;
    for (const it of items) {
      const r = (it.reason || '').trim();
      if (!r) continue;
      const c = (reasonCounts.get(r) || 0) + 1;
      reasonCounts.set(r, c);
      if (c > topCount) { topCount = c; topReason = r; }
    }
    // Earliest and latest expiry among items (if any)
    let earliest = null;
    let latest = null;
    let expiredCount = 0;
    for (const it of items) {
      const ts = it.expiresAt;
      if (!ts) continue;
      let ms = null;
      try {
        if (typeof ts.toMillis === 'function') ms = ts.toMillis();
        else if (ts.seconds) ms = ts.seconds * 1000;
        else {
          const d = new Date(ts);
          if (!isNaN(d.getTime())) ms = d.getTime();
        }
      } catch {}
      if (typeof ms === 'number') {
        if (earliest == null || ms < earliest) earliest = ms;
        if (latest == null || ms > latest) latest = ms;
        if (ms <= Date.now()) expiredCount++;
      }
    }
    // Created by summary (single value or 'Mixed')
    const creatorCounts = new Map();
    let creatorTop = '';
    let creatorTopCount = 0;
    for (const it of items) {
      const u = (it.createdBy || '').trim();
      if (!u) continue;
      const c = (creatorCounts.get(u) || 0) + 1;
      creatorCounts.set(u, c);
      if (c > creatorTopCount) { creatorTopCount = c; creatorTop = u; }
    }
    const creatorsDistinct = creatorCounts.size;
    const createdBy = creatorsDistinct <= 1 ? (creatorTop || '') : 'Mixed';
    const activeCount = count - expiredCount; // note: items without expiresAt are considered active (no expiry)
    return { holdName, count, boothIds, reason: topReason || '', expiresAtMs: earliest, latestExpiresAtMs: latest, expiredCount, activeCount, createdBy };
  });
  arr.sort((a, b) => a.holdName.localeCompare(b.holdName));
  return arr;
};

/**
 * Clear all On-Hold entries for a given holdName (event/layout).
 */
export const clearHoldGroupByName = async ({ eventId, layoutId, holdName }) => {
  if (!db) throw new Error('Database not initialized');
  if (!eventId || !layoutId || !holdName) return 0;
  const col = collection(db, COLLECTION);
  const q = query(col, where('eventId', '==', eventId), where('layoutId', '==', layoutId));
  const snap = await getDocs(q);
  const batch = writeBatch(db);
  let count = 0;
  let layout = null;
  try { layout = await getLayoutCached(layoutId); } catch {}
  snap.forEach(d => {
    const data = d.data() || {};
    if (data.status === 'On-Hold' && String(data.holdName) === String(holdName)) {
      try {
        const logRef = newReservationLogRef();
        writeReservationLogTx(batch, logRef, {
          type: 'hold-cleared-group',
          reservationId: d.ref.id,
          eventId,
          layoutId,
          boothId: data.boothId,
          ...(resolveBoothNumber(layout, data.boothId) != null ? { boothNumber: resolveBoothNumber(layout, data.boothId) } : {}),
          previousStatus: data.status,
          previousVendorId: data.vendorId || null,
          previousCreatedBy: data.createdByProfileId || null,
          previousSessionId: data.sessionId || null,
          previousCreatedAt: data.createdAt || null,
          previousExpiresAt: data.expiresAt || null,
          triggeredBy: 'clearHoldGroupByName',
          triggeredByProfileId: null,
          metadata: { holdName },
        });
      } catch {}
      batch.delete(d.ref);
      count++;
    }
  });
  if (count > 0) await batch.commit();
  return count;
};

/**
 * Cleanup: Clear expired On-Hold entries for a layout (optionally restricted to boothIds).
 * Returns the number of documents deleted.
 */
export const clearExpiredHolds = async ({ eventId, layoutId, boothIds = undefined }) => {
  if (!db) throw new Error('Database not initialized');
  if (!eventId || !layoutId) return 0;
  const now = Date.now();
  const idsFilter = Array.isArray(boothIds) && boothIds.length > 0 ? new Set(boothIds) : null;
  const col = collection(db, COLLECTION);
  const q = query(col, where('eventId', '==', eventId), where('layoutId', '==', layoutId));
  const snap = await getDocs(q);
  const batch = writeBatch(db);
  let count = 0;
  let layout = null;
  try { layout = await getLayoutCached(layoutId); } catch {}
  snap.forEach(d => {
    const data = d.data() || {};
    if (data.status !== 'On-Hold') return;
    if (idsFilter && !idsFilter.has(data.boothId)) return;
    const ts = data.expiresAt;
    let ms = null;
    try {
      if (ts && typeof ts.toMillis === 'function') ms = ts.toMillis();
      else if (ts && ts.seconds) ms = ts.seconds * 1000;
      else if (ts) {
        const date = new Date(ts);
        if (!isNaN(date.getTime())) ms = date.getTime();
      }
    } catch {}
    if (typeof ms === 'number' && ms <= now) {
      // Log expired hold cleanup
      try {
        const logRef = newReservationLogRef();
        writeReservationLogTx(batch, logRef, {
          type: 'expired-cleanup-on-hold',
          reservationId: d.ref.id,
          eventId,
          layoutId,
          boothId: data.boothId,
          ...(resolveBoothNumber(layout, data.boothId) != null ? { boothNumber: resolveBoothNumber(layout, data.boothId) } : {}),
          previousStatus: data.status,
          previousVendorId: data.vendorId || null,
          previousCreatedBy: data.createdByProfileId || null,
          previousSessionId: data.sessionId || null,
          previousCreatedAt: data.createdAt || null,
          previousExpiresAt: data.expiresAt || null,
          expiredAt: serverTimestamp(),
          triggeredBy: 'clearExpiredHolds',
          triggeredByProfileId: null,
          metadata: {},
        });
      } catch {}
      batch.delete(d.ref);
      count++;
    }
  });
  if (count > 0) await batch.commit();
  return count;
};

/**
 * Cleanup: Clear expired Pending reservations for a layout (optionally restricted to boothIds).
 * Returns the number of documents deleted.
 */
export const clearExpiredPendingReservations = async ({ eventId }) => {
  if (!db) throw new Error('Database not initialized');
  if (!eventId) return 0;
  const now = Date.now();
  const col = collection(db, COLLECTION);
  const q = query(col, where('eventId', '==', eventId), where('status', '==', 'Pending'));
  const snap = await getDocs(q);

  if (snap.empty) return 0;

  let deleted = 0;
  const docs = snap.docs;
  const chunkSize = 450; // batch size under Firestore limit
  for (let i = 0; i < docs.length; i += chunkSize) {
    const batch = writeBatch(db);
    const slice = docs.slice(i, i + chunkSize);
    for (const d of slice) {
      try {
        const data = d.data() || {};
        const ts = data.expiresAt;
        let ms = null;
        if (ts) {
          try {
            // Firestore Timestamp
            if (typeof ts.toMillis === 'function') ms = ts.toMillis();
            // Proto-like object { seconds }
            else if (ts.seconds) ms = ts.seconds * 1000;
            else if (typeof ts === 'number') ms = ts; // already ms
            else if (typeof ts === 'string') {
              // Try direct parse first
              const p = Date.parse(ts);
              if (!isNaN(p)) {
                ms = p;
              } else {
                // Handle formats like "February 19, 2026 at 5:44:12 PM UTC-6"
                const m = String(ts).match(/(.+?)\s*UTC([+-]?\d{1,2})\b/i);
                if (m) {
                  const datePart = m[1].replace(/\s+at\s+/i, ' ');
                  const offsetHours = parseInt(m[2], 10);
                  const parsed = Date.parse(datePart + ' UTC');
                  if (!isNaN(parsed)) {
                    // parsed treats datePart as UTC; to get true UTC, subtract the offset hours
                    ms = parsed - (offsetHours * 60 * 60 * 1000);
                  }
                } else {
                  // Fallback: try generic Date constructor
                  const dt = new Date(ts);
                  if (!isNaN(dt.getTime())) ms = dt.getTime();
                }
              }
            } else {
              const dt = new Date(ts);
              if (!isNaN(dt.getTime())) ms = dt.getTime();
            }
          } catch (parseErr) {
            // ignore parse errors for this doc
          }
        }
        if (typeof ms === 'number' && ms <= now) {
          batch.delete(d.ref);
          deleted++;
        }
      } catch (e) {
        // ignore individual doc errors and continue
        console.warn('[clearExpiredPendingReservations] skipping doc due to error', e?.message || e);
      }
    }
    await batch.commit();
  }
  return deleted;
};

/**
 * Session takeover cleanup: remove Pending reservations owned by the same vendor but from a different session.
 * Logs each removal as 'expired-cleanup-session-takeover'. Returns number of reservations cleared.
 */
export const clearOtherSessionPendingReservations = async ({ vendorId, createdByProfileId, newSessionId }) => {
  if (!db) throw new Error('Database not initialized');
  if (!vendorId || !newSessionId) return 0;
  const col = collection(db, COLLECTION);
  // Query all reservations owned by this vendor. (We don't filter by session to inspect old sessions.)
  const q = query(col, where('vendorId', '==', vendorId));
  const snap = await getDocs(q);
  const batch = writeBatch(db);
  let count = 0;
  // Build boothId -> boothNumber maps per layout to enrich logs
  const layoutIds = Array.from(new Set(snap.docs.map(d => (d.data() || {}).layoutId).filter(Boolean)));
  const layoutBoothMaps = new Map(); // layoutId -> { boothId: boothNumber }
  for (const lid of layoutIds) {
    try {
      const ly = await getLayoutPublic(lid);
      const cells = Array.isArray(ly?.cellData) ? ly.cellData : [];
      const map = {};
      cells.forEach(c => {
        if (c && c.id) {
          map[c.id] = c.number != null ? c.number : (c.boothNumber != null ? c.boothNumber : c.id);
        }
      });
      layoutBoothMaps.set(lid, map);
    } catch {}
  }
  snap.forEach(d => {
    const data = d.data() || {};
    if (data.status !== 'Pending') return;
    if (!data.sessionId || data.sessionId === newSessionId) return; // skip current session or missing session id
    // Resolve boothNumber via layout map, with graceful fallbacks
    let boothNumberMeta = null;
    try {
      const m = layoutBoothMaps.get(data.layoutId || '');
      const found = m && m[data.boothId];
      if (found != null) boothNumberMeta = found;
      else if (typeof data.boothNumber !== 'undefined') boothNumberMeta = data.boothNumber;
      else if (typeof data.number !== 'undefined') boothNumberMeta = data.number;
      else if (typeof data.boothId === 'string' && /^\d+$/.test(data.boothId)) boothNumberMeta = parseInt(data.boothId, 10);
    } catch {}
    try {
      const logRef = newReservationLogRef();
      writeReservationLogTx(batch, logRef, {
        type: 'session-logout',
        reservationId: d.ref.id,
        eventId: data.eventId || null,
        layoutId: data.layoutId || null,
        boothId: data.boothId || null,
        ...(boothNumberMeta != null ? { boothNumber: boothNumberMeta } : {}),
        previousStatus: data.status,
        previousVendorId: data.vendorId || null,
        previousCreatedBy: data.createdByProfileId || null,
        previousSessionId: data.sessionId || null,
        previousCreatedAt: data.createdAt || null,
        previousExpiresAt: data.expiresAt || null,
        expiredAt: serverTimestamp(),
        triggeredBy: 'session-takeover',
        triggeredByProfileId: createdByProfileId,
        // Put boothId in metadata for auxiliary details; top-level boothNumber is used for the column
        metadata: { newSessionId, boothId: data.boothId || null },
      });
    } catch {}
    batch.delete(d.ref);
    count++;
  });
  if (count > 0) await batch.commit();
  return count;
};

/**
 * Load all Reserved booths for a specific vendor (vendorId).
 * Returns an array of reservation objects with booth details grouped by event.
 * Also fetches transaction data to get the actual price paid per booth.
 * NOTE: Now queries by vendorId instead of profileId to support multi-user vendors.
 */
export const loadMyReservedBooths = async ({ vendorId }) => {
  if (!db) throw new Error('Database not initialized');
  if (!vendorId) {
    return [];
  }
 
  const col = collection(db, COLLECTION);
  const q = query(
    col,
    where('vendorId', '==', vendorId),
    where('status', '==', 'Reserved')
  );
  
  try {
    const snap = await getDocs(q);
  
    // For now, we won't query transactions by metadata.vendorId as it requires a composite index
    // Instead, we'll get pricing from the layout's booth data
    const reservations = [];
  
    for (const docSnap of snap.docs) {
      const data = docSnap.data();
      const { eventId, layoutId, boothId, createdAt, updatedAt, price, boothNumber: storedBoothNumber, level: storedLevel, corner: storedCorner, row: storedRow, col: storedCol } = data;
      // paymentMethod may be stored under several keys depending on how the reservation/transaction was created
      const paymentMethod = data.paymentMethod || data.paymentType || (data.metadata && data.metadata.paymentType) || null;
    
      // Use stored booth details from reservation (historical record)
      // Only fetch layout for any missing details or as fallback
      let boothNumber = storedBoothNumber || boothId;
      let boothDetails = {
        id: boothId,
        number: storedBoothNumber,
        boothNumber: storedBoothNumber,
        level: storedLevel,
        corner: storedCorner,
        row: storedRow,
        col: storedCol,
        price
      };
    
      // If we're missing critical details, try to get them from layout
      if (!storedBoothNumber || !storedLevel) {
        try {
          const layout = await getLayoutCached(layoutId);
          if (layout?.cellData) {
            const layoutBooth = layout.cellData.find(cell => cell.id === boothId);
            if (layoutBooth) {
              boothNumber = storedBoothNumber || layoutBooth.number || layoutBooth.boothNumber || boothId;
              boothDetails = {
                ...layoutBooth,
                // Override with stored values (historical record takes precedence)
                ...(price != null && { price }),
                ...(storedBoothNumber != null && { number: storedBoothNumber, boothNumber: storedBoothNumber }),
                ...(storedLevel != null && { level: storedLevel }),
                ...(storedCorner != null && { corner: storedCorner }),
                ...(storedRow != null && { row: storedRow }),
                ...(storedCol != null && { col: storedCol }),
              };
            } else {
              console.warn('[loadMyReservedBooths] Booth not found in cellData:', boothId);
            }
          } else {
            console.warn('[loadMyReservedBooths] No cellData in layout:', layoutId);
          }
        } catch (e) {
          console.warn('[loadMyReservedBooths] Failed to load layout for enrichment:', e);
        }
      } else {
        // do nothing. We have all details from stored data.  
        null;
      }
    
      reservations.push({
        id: docSnap.id,
        eventId,
        layoutId,
        boothId,
        boothNumber,
        boothDetails,
        paymentMethod,
        createdAt,
        updatedAt,
        status: 'Reserved'
      });
    }
  
    return reservations;
  } catch (error) {
    console.error('[loadMyReservedBooths] Query failed:', error);
    console.error('[loadMyReservedBooths] Error code:', error.code);
    console.error('[loadMyReservedBooths] Error message:', error.message);
    throw error;
  }
};

/**
 * Return unique vendorIds that have one or more Reserved booths.
 * If eventId is provided, scope to that event only.
 */
export const listReservedVendorIds = async ({ eventId } = {}) => {
  if (!db) throw new Error('Database not initialized');

  const col = collection(db, COLLECTION);
  const q = eventId
    ? query(col, where('eventId', '==', eventId))
    : query(col, where('status', '==', 'Reserved'));

  const snap = await getDocs(q);
  const ids = new Set();
  snap.forEach((docSnap) => {
    const data = docSnap.data() || {};
    if (eventId && data.status !== 'Reserved') return;
    const vendorId = String(data.vendorId || '').trim();
    if (vendorId) ids.add(vendorId);
  });
  return ids;
};
